/**
 * Site benefits.
 *
 * A user's real access is decided in the database from three stacked layers:
 *   1. Local override — an admin set this benefit for this person
 *   2. Else Patreon — they have a Script Hive campaign tier that is in the matrix
 *   3. Else the site default
 *
 * Linking Patreon does not grant anything by itself. Layer 2 only applies when
 * they are on a campaign tier that has a row in the matrix and their OAuth
 * tokens still work. A $0 tier still counts; no campaign tier, a stored tier
 * not in the matrix yet, or valid_tokens = false skips layer 2 and they get
 * defaults + any local overrides (same as unlinked).
 *
 * Pages should not re-decide benefits. Read the already-figured-out map on
 * the user (`user.benefits`) via userHasBenefit / getUserDownloadsPerDay.
 */

import supabase from "./Supabase";

export type BenefitValueType = "boolean" | "number";

export type BenefitValue = boolean | number;

export type BenefitId =
    | "darkMode"
    | "roulette"
    | "scriptFavoriting"
    | "scriptRequests"
    | "downloadsPerDay"
    | "platinumAccess";

export type BenefitDefinition = {
    id: BenefitId;
    label: string;
    description?: string;
    valueType: BenefitValueType;
    defaultValue: BenefitValue;
};

export type BenefitMap = Record<BenefitId, BenefitValue>;

/** Which layer supplied the value the user actually gets. */
export type BenefitSource = "local" | "patreon" | "default";

export type BenefitResolution = {
    value: BenefitValue;
    source: BenefitSource;
};

export type BenefitsById = Record<BenefitId, BenefitResolution>;

/** Settings grouped by Patreon tier id. */
export type BenefitsByTier = Record<string, Partial<BenefitMap>>;

/** The named benefits the site knows about. Keep ids in sync with the database. */
export const BENEFIT_REGISTRY: readonly BenefitDefinition[] = [
    {
        id: "darkMode",
        label: "Dark mode",
        description: "Access to the site's 'Dark Mode' color scheme.",
        valueType: "boolean",
        defaultValue: false,
    },
    {
        id: "roulette",
        label: "Roulette",
        description: "Access to Script Roulette feature.",
        valueType: "boolean",
        defaultValue: false,
    },
    {
        id: "scriptFavoriting",
        label: "Script favoriting",
        description: "Ability to add scripts to a your favorites list.",
        valueType: "boolean",
        defaultValue: false,
    },
    {
        id: "scriptRequests",
        label: "Script requests",
        description: "Ability to submit script requests to the site.",
        valueType: "boolean",
        defaultValue: false,
    },
    {
        id: "downloadsPerDay",
        label: "Downloads per day",
        description: "Maximum number of scripts that can be downloaded per day.",
        valueType: "number",
        defaultValue: 25,
    },
    {
        id: "platinumAccess",
        label: "Platinum access",
        description: "Platinum-tier status and features.",
        valueType: "boolean",
        defaultValue: false,
    },
] as const;

export const BENEFIT_IDS: readonly BenefitId[] = BENEFIT_REGISTRY.map((benefit) => benefit.id);

export function getBenefit(id: BenefitId): BenefitDefinition {
    const benefit = BENEFIT_REGISTRY.find((entry) => entry.id === id);
    if (!benefit) {
        throw new Error(`Unknown benefit: ${id}`);
    }
    return benefit;
}

/** Hardcoded fallbacks used if the database defaults have not loaded yet. */
export function getDefaultBenefits(): BenefitMap {
    return BENEFIT_REGISTRY.reduce((map, benefit) => {
        map[benefit.id] = benefit.defaultValue;
        return map;
    }, {} as BenefitMap);
}

export function isValidBenefitValue(benefitId: BenefitId, value: unknown): value is BenefitValue {
    const benefit = getBenefit(benefitId);
    if (benefit.valueType === "boolean") {
        return typeof value === "boolean";
    }
    return typeof value === "number" && Number.isFinite(value) && Number.isInteger(value) && value >= 0;
}

export function parseBenefitRpcValue(benefit: BenefitDefinition, raw: unknown): BenefitValue {
    if (benefit.valueType === "boolean") {
        return raw === true;
    }
    const numberValue = typeof raw === "number" ? raw : Number(raw);
    if (Number.isFinite(numberValue) && Number.isInteger(numberValue) && numberValue >= 0) {
        return numberValue;
    }
    return benefit.defaultValue;
}

/**
 * Turn the database helper's JSON ({ darkMode: { value, source }, ... })
 * into a typed map. Missing entries fall back to the hardcoded defaults.
 */
export function parseBenefitsFromRpc(payload?: Record<string, { value?: unknown; source?: string } | unknown> | null): BenefitsById {
    const defaults = getDefaultBenefits();
    const parsed = {} as BenefitsById;

    for (const benefit of BENEFIT_REGISTRY) {
        const entry = payload?.[benefit.id];
        if (entry && typeof entry === "object" && "value" in (entry as object)) {
            const typed = entry as { value?: unknown; source?: string };
            const source: BenefitSource =
                typed.source === "local" || typed.source === "patreon" ? typed.source : "default";
            parsed[benefit.id] = {
                value: parseBenefitRpcValue(benefit, typed.value),
                source,
            };
        } else {
            parsed[benefit.id] = {
                value: defaults[benefit.id],
                source: "default",
            };
        }
    }

    return parsed;
}

export function benefitsMapFromResolved(resolved?: BenefitsById | null): BenefitMap {
    const defaults = getDefaultBenefits();
    if (!resolved) {
        return defaults;
    }

    const map = { ...defaults };
    for (const benefit of BENEFIT_REGISTRY) {
        const entry = resolved[benefit.id];
        if (entry) {
            map[benefit.id] = entry.value;
        }
    }
    return map;
}

type UserBenefitSource = {
    benefits?: BenefitsById | BenefitMap | null;
} | null | undefined;

function isResolvedBenefits(value: BenefitsById | BenefitMap): value is BenefitsById {
    const first = value[BENEFIT_IDS[0]];
    return typeof first === "object" && first !== null && "value" in first;
}

/** The actual on/off and number values this user currently gets. */
export function resolveUserBenefits(user: UserBenefitSource): BenefitMap {
    if (!user?.benefits) {
        return getDefaultBenefits();
    }
    if (isResolvedBenefits(user.benefits)) {
        return benefitsMapFromResolved(user.benefits);
    }
    return {
        ...getDefaultBenefits(),
        ...user.benefits,
    };
}

/** True if this on/off benefit is actually on for the user. */
export function userHasBenefit(
    user: UserBenefitSource,
    benefitId: Exclude<BenefitId, "downloadsPerDay">,
): boolean {
    return resolveUserBenefits(user)[benefitId] === true;
}

/** Daily download cap from the resolved benefits. Admins may still bypass this elsewhere. */
export function getUserDownloadsPerDay(user: UserBenefitSource): number {
    const value = resolveUserBenefits(user).downloadsPerDay;
    return typeof value === "number" && Number.isFinite(value) ? value : 25;
}

/** Live daily download cap from the database. Null if the helper cannot be reached. */
export async function fetchUserDownloadsPerDay(userId: string): Promise<number | null> {
    const { data, error } = await supabase.rpc("get_user_benefits", {
        p_user_id: userId,
    });

    if (error || !data) {
        console.error("Error loading download limit:", error);
        return null;
    }

    return getUserDownloadsPerDay({ benefits: parseBenefitsFromRpc(data) });
}

/** True if they have anything above a normal (default) account — used for Account badges. */
export function userHasElevatedAccess(user: UserBenefitSource): boolean {
    const features = resolveUserBenefits(user);
    const freeDownloads = getBenefit("downloadsPerDay").defaultValue as number;
    return (
        features.platinumAccess === true
        || features.roulette === true
        || features.scriptFavoriting === true
        || features.scriptRequests === true
        || (typeof features.downloadsPerDay === "number" && features.downloadsPerDay > freeDownloads)
    );
}

export type DisplayedBenefit = {
    id: BenefitId;
    label: string;
    display: string;
    source: BenefitSource;
};

/**
 * Admin Users tags: only extras the person actually gets.
 *
 * - Skip anything that matches the site default (nothing extra to show).
 * - On/off: a tag means the feature is on. "Off" is never a tag.
 * - Numbers: show the number only when it is not the default.
 * - Color comes from which layer won: local (admin) vs Patreon.
 */
export function getDisplayedBenefits(
    resolved?: BenefitsById | null,
    defaults?: BenefitMap | null,
): DisplayedBenefit[] {
    const baseline = defaults ?? getDefaultBenefits();
    const possessed: DisplayedBenefit[] = [];

    if (!resolved) {
        return possessed;
    }

    for (const benefit of BENEFIT_REGISTRY) {
        const entry = resolved[benefit.id];
        if (!entry) {
            continue;
        }
        if (entry.source === "default") {
            continue;
        }
        if (entry.value === baseline[benefit.id]) {
            continue;
        }
        if (benefit.valueType === "boolean" && entry.value !== true) {
            continue;
        }

        possessed.push({
            id: benefit.id,
            label: benefit.label,
            display: benefit.valueType === "number"
                ? `${benefit.label}: ${entry.value}`
                : benefit.label,
            source: entry.source,
        });
    }

    return possessed;
}

/** Fill any missing cells in a Patreon matrix column so every benefit has a value. */
export function mergeTierBenefits(stored?: Partial<BenefitMap> | null): BenefitMap {
    const defaults = getDefaultBenefits();
    if (!stored) {
        return defaults;
    }

    const merged = { ...defaults };
    for (const benefit of BENEFIT_REGISTRY) {
        const value = stored[benefit.id];
        if (value === undefined || value === null) {
            continue;
        }
        if (benefit.valueType === "boolean" && typeof value === "boolean") {
            merged[benefit.id] = value;
        } else if (benefit.valueType === "number" && typeof value === "number" && Number.isFinite(value)) {
            merged[benefit.id] = value;
        }
    }
    return merged;
}

/** Admin > Benefits: current site-wide defaults (layer 1). */
export async function getBenefitDefaults(): Promise<BenefitMap | null> {
    const { data, error } = await supabase.rpc("get_benefit_defaults");
    if (error || !data) {
        console.error("Error loading benefit defaults:", error);
        return null;
    }

    const defaults = getDefaultBenefits();
    for (const benefit of BENEFIT_REGISTRY) {
        if (data[benefit.id] !== undefined) {
            defaults[benefit.id] = parseBenefitRpcValue(benefit, data[benefit.id]);
        }
    }
    return defaults;
}

/** Admin > Benefits: every benefit must have a value. There is no unset here. */
export async function saveBenefitDefaults(defaults: BenefitMap): Promise<BenefitMap | null> {
    const { data, error } = await supabase.rpc("admin_save_benefit_defaults", { defaults });
    if (error || !data) {
        console.error("Error saving benefit defaults:", error);
        return null;
    }

    const saved = getDefaultBenefits();
    for (const benefit of BENEFIT_REGISTRY) {
        if (data[benefit.id] !== undefined) {
            saved[benefit.id] = parseBenefitRpcValue(benefit, data[benefit.id]);
        }
    }
    return saved;
}

/** Admin Users popup: set or clear local overrides. JSON null means "unset / inherit". */
export async function saveUserBenefitOverrides(
    userId: string,
    overrides: Record<string, BenefitValue | null>,
): Promise<BenefitsById | null> {
    const { data, error } = await supabase.rpc("admin_set_user_benefit_overrides", {
        p_user_id: userId,
        overrides,
    });
    if (error || !data) {
        console.error("Error saving benefit overrides:", error);
        return null;
    }
    return parseBenefitsFromRpc(data);
}
